import type { PricingConfig } from "./types.ts"

export interface HireCharge {
  unitPricePence: number
  billableDays: number
  chargedDays: number
  rateBasis: "daily" | "two_day" | "short_hire" | "weekly" | "long_term_weekly"
  ratePence: number
  weeklyCapApplied: boolean
  weeklyMinimumApplied: boolean
  excludedDeliveryDay: boolean
  excludedCollectionDay: boolean
}

/** Preserve existing displayed durations independently of billing exclusions. */
export function billableHireDays(hireDays: number, config: PricingConfig): number {
  return config.model === "daily_weekly_inclusive" && config.short_hire_excludes_logistics_days && hireDays < 7
    ? Math.max(1, hireDays - 2)
    : hireDays
}

/** Round a pro-rated weekly unit price to 50p, with exact ties going to the even increment. */
export function calculateWeeklyHirePence(hireDays: number, weeklyPence: number): number {
  const numerator = hireDays * weeklyPence
  if (hireDays % 7 === 0) return numerator / 7
  // Work with the exact fraction in 50p units to avoid rounding to pennies first.
  const denominator = 7 * 50
  const lower = Math.floor(numerator / denominator)
  const twiceRemainder = (numerator % denominator) * 2
  const roundUp = twiceRemainder > denominator || (twiceRemainder === denominator && lower % 2 !== 0)
  return (lower + (roundUp ? 1 : 0)) * 50
}

/** One shared integer-pence calculation for browser previews and server quotes. */
export function calculateHireCharge(hireDays: number, config: PricingConfig): HireCharge {
  if (!Number.isInteger(hireDays) || hireDays < 0) throw new Error("INVALID_HIRE_DAYS")
  const billableDays = config.model === "daily_weekly_inclusive" && hireDays >= 7
    ? hireDays - (config.billing_policy?.weekly_excludes_delivery_day ? 1 : 0)
    : billableHireDays(hireDays, config)
  const charge: HireCharge = {
    unitPricePence: 0, billableDays, chargedDays: billableDays,
    rateBasis: "weekly", ratePence: config.weekly_unit_price_pence,
    weeklyCapApplied: false, weeklyMinimumApplied: false,
    excludedDeliveryDay: config.model !== "daily_weekly_inclusive"
      || (hireDays < 7 ? Boolean(config.short_hire_excludes_logistics_days) : Boolean(config.billing_policy?.weekly_excludes_delivery_day)),
    excludedCollectionDay: config.model !== "daily_weekly_inclusive"
      || (hireDays < 7 && Boolean(config.short_hire_excludes_logistics_days)),
  }
  if (config.model === "daily_weekly_inclusive") {
    if (hireDays < 1) throw new Error("INVALID_HIRE_DAYS")
    if (config.short_hire_excludes_logistics_days && hireDays < 2) throw new Error("HIRE_WINDOW_TOO_SHORT")
    if (hireDays < (config.minimum_hire_days ?? 1)) throw new Error("HIRE_WINDOW_TOO_SHORT")
    if (hireDays < 7) {
      if (config.one_day_unit_price_pence === null) throw new Error("OPTION_DISABLED")
      const schedule = config.billing_policy?.version === 2 ? config.billing_policy.short_hire_prices_pence : undefined
      const shortPrice = schedule ? schedule[billableDays - 1] : billableDays * config.one_day_unit_price_pence
      charge.weeklyCapApplied = config.billing_policy?.short_hire_cap === "standard_week" && shortPrice > config.weekly_unit_price_pence
      charge.rateBasis = charge.weeklyCapApplied ? "weekly" : schedule ? "short_hire" : "daily"
      charge.ratePence = charge.weeklyCapApplied ? config.weekly_unit_price_pence : schedule ? shortPrice : config.one_day_unit_price_pence
      charge.unitPricePence = charge.weeklyCapApplied ? config.weekly_unit_price_pence : shortPrice
      return charge
    }
    // Tier eligibility always uses calendar days, before exclusions/minimums.
    const longHire = hireDays >= config.long_term_minimum_days!
    charge.rateBasis = longHire ? "long_term_weekly" : "weekly"
    charge.ratePence = longHire
      ? config.long_term_weekly_unit_price_pence!
      : config.weekly_unit_price_pence
    charge.chargedDays = Math.max(billableDays, config.billing_policy?.weekly_minimum_charge_days ?? 1)
    charge.weeklyMinimumApplied = charge.chargedDays > billableDays
    const proratedPrice = calculateWeeklyHirePence(charge.chargedDays, charge.ratePence)
    // Preserve the existing 50p calculation, then round a half pound up per unit.
    charge.unitPricePence = config.billing_policy?.version === 2 && config.billing_policy.round_half_pounds_up
      ? Math.ceil(proratedPrice / 100) * 100 : proratedPrice
    return charge
  }
  if (hireDays <= 1) {
    if (config.one_day_unit_price_pence === null) throw new Error("OPTION_DISABLED")
    charge.rateBasis = "daily"
    charge.ratePence = config.one_day_unit_price_pence
    charge.unitPricePence = config.one_day_unit_price_pence
  } else if (hireDays === 2) {
    if (config.two_day_unit_price_pence === null) throw new Error("OPTION_DISABLED")
    charge.rateBasis = "two_day"
    charge.ratePence = config.two_day_unit_price_pence
    charge.unitPricePence = config.two_day_unit_price_pence
  } else {
    charge.unitPricePence = hireDays <= 7 ? config.weekly_unit_price_pence : calculateWeeklyHirePence(hireDays, config.weekly_unit_price_pence)
  }
  return charge
}

export function calculateUnitHirePence(hireDays: number, config: PricingConfig): number {
  return calculateHireCharge(hireDays, config).unitPricePence
}
